import { useState, useEffect, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { currency, Card, Button } from "@repo/o37-ui";
import { useToast } from "@repo/o37-ui";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type ForecastData = {
  month: string;
  weightedRevenue: number;
  totalRevenue: number;
};

type ForecastFilter = 'this_month' | 'next_month' | 'this_year' | 'all';

export default function Forecasting({ partnerId, allowEditing = false }: { partnerId?: string; allowEditing?: boolean }) {
  const [data, setData] = useState<ForecastData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ForecastFilter>('all');
  const [goal, setGoal] = useState<number>(0);
  const [savingGoal, setSavingGoal] = useState(false);
  const [isAnnualized, setIsAnnualized] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    loadForecast();
    loadGoal();
  }, [partnerId, isAnnualized]);

  const loadGoal = async () => {
    try {
      let url = `${API_URL}/settings/goal`;
      if (partnerId) {
        url = `${API_URL}/partners/${partnerId}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        const rawGoal = partnerId ? (result.salesGoal || 0) : (result.goal || 0);
        // If not annualized, goal should reflect monthly target (approx / 12)
        setGoal(isAnnualized ? rawGoal : Math.round(rawGoal / 12));
      }
    } catch (error) {
      console.error("Failed to load goal:", error);
    }
  };

  const saveGoal = async (newGoal: number) => {
    if (!allowEditing) return;
    setSavingGoal(true);
    try {
      // Always save as annualized goal
      const annualGoal = isAnnualized ? newGoal : newGoal * 12;
      
      if (partnerId) {
        // Save partner goal
        const response = await fetch(`${API_URL}/partners/${partnerId}`, {
          method: "PATCH",
          headers: { 
            "Content-Type": "application/json",
            "x-admin-email": localStorage.getItem("partner_email") || "" 
          },
          body: JSON.stringify({ salesGoal: annualGoal }),
        });
        if (!response.ok) throw new Error("Failed to save partner goal");
      } else {
        // Save global goal
        const response = await fetch(`${API_URL}/settings/goal`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-admin-email": localStorage.getItem("partner_email") || "" 
          },
          body: JSON.stringify({ goal: annualGoal }),
        });
        if (!response.ok) throw new Error("Failed to save global goal");
      }
    } catch (error) {
      console.error("Failed to save goal:", error);
      showToast("Failed to save goal", "error");
    } finally {
      setSavingGoal(false);
    }
  };

  const loadForecast = async () => {
    setLoading(true);
    try {
      const url = partnerId 
        ? `${API_URL}/forecasting?partnerId=${partnerId}&annualized=${isAnnualized}`
        : `${API_URL}/forecasting?annualized=${isAnnualized}`;
        
      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Failed to load forecast:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const getMonthStr = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
    };

    const thisMonthStr = getMonthStr(now);
    
    const nextMonthDate = new Date(now);
    nextMonthDate.setMonth(currentMonth + 1);
    const nextMonthStr = getMonthStr(nextMonthDate);

    switch (filter) {
      case 'this_month':
        return data.filter(d => d.month === thisMonthStr);
      case 'next_month':
        return data.filter(d => d.month === nextMonthStr);
      case 'this_year':
        return data.filter(d => d.month.startsWith(`${currentYear}-`));
      default:
        return data;
    }
  }, [data, filter]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/80 backdrop-blur-md border border-white/20 p-4 rounded-xl shadow-xl ring-1 ring-black/5">
          <p className="font-semibold text-neutral-900 mb-2">{label}</p>
          {payload.map((p: any, index: number) => (
            <div key={index} className="text-sm flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }}></span>
              <span className="text-neutral-600">{p.name}:</span>
              <span className="font-medium text-neutral-900">{currency(p.value)}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-neutral-600">Loading Forecast...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[13px] uppercase tracking-wider text-neutral-500">Forecasting</h2>
        <div className="flex items-center gap-3">
          {/* Annualized Toggle */}
          <div className="flex items-center bg-neutral-100 rounded-lg p-1">
            <Button
              variant={isAnnualized ? 'outline' : 'ghost'}
              size="sm"
              onClick={() => setIsAnnualized(true)}
              className={`text-xs h-7 ${isAnnualized ? 'bg-white shadow-sm text-neutral-900 font-medium border-transparent' : 'text-neutral-500 hover:text-neutral-700 bg-transparent border-transparent'}`}
              text="Annualized"
            />
            <Button
              variant={!isAnnualized ? 'outline' : 'ghost'}
              size="sm"
              onClick={() => setIsAnnualized(false)}
              className={`text-xs h-7 ${!isAnnualized ? 'bg-white shadow-sm text-neutral-900 font-medium border-transparent' : 'text-neutral-500 hover:text-neutral-700 bg-transparent border-transparent'}`}
              text="Instant"
            />
          </div>

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as ForecastFilter)}
            className="border border-neutral-300 rounded-xl px-3 py-1.5 text-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-800"
          >
            <option value="this_month">This Month</option>
            <option value="next_month">Next Month</option>
            <option value="this_year">Remainder of Year</option>
            <option value="all">Next 12 Months</option>
          </select>
        </div>
      </div>

      <Card className="p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider">
            {filter === 'this_month' ? `Expected Revenue (This Month) - ${isAnnualized ? 'Annualized' : 'Instant'}` :
             filter === 'next_month' ? `Expected Revenue (Next Month) - ${isAnnualized ? 'Annualized' : 'Instant'}` :
             filter === 'this_year' ? `Expected Revenue (Remainder of Year) - ${isAnnualized ? 'Annualized' : 'Instant'}` :
             `Expected Revenue (Next 12 Months) - ${isAnnualized ? 'Annualized' : 'Instant'}`}
          </h3>
          
          {allowEditing && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-neutral-500">Goal:</span>
              <div className="flex items-center gap-1">
                <span className="text-sm text-neutral-400">$</span>
                <input
                  type="number"
                  value={goal}
                  onChange={(e) => setGoal(Number(e.target.value))}
                  onBlur={() => saveGoal(goal)}
                  className="w-24 border-b border-neutral-300 focus:border-neutral-900 focus:outline-none text-right font-medium text-sm py-0.5"
                />
                <span className="text-xs text-neutral-400">/{isAnnualized ? 'yr' : 'mo'}</span>
              </div>
              {savingGoal && <span className="text-xs text-neutral-400 animate-pulse">Saving...</span>}
            </div>
          )}
        </div>

        <div className="h-[400px] w-full">
            {filteredData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-neutral-400 italic">
                No forecast data available for this period.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="month" 
                    stroke="#888888" 
                    fontSize={12} 
                    tickLine={false} 
                    axisLine={false} 
                    tickFormatter={(value) => {
                      // value is "YYYY-MM"
                      if (typeof value === 'string' && value.includes('-')) {
                        const [, m] = value.split('-');
                        const date = new Date();
                        date.setMonth(parseInt(m) - 1);
                        return date.toLocaleString('en-US', { month: 'short' });
                      }
                      return value;
                    }}
                  />
                  <YAxis 
                    stroke="#888888" 
                    fontSize={12} 
                    tickLine={false} 
                    axisLine={false} 
                    tickFormatter={(value) => {
                      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
                      if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
                      return `$${value}`;
                    }} 
                  />
                  <Tooltip 
                    cursor={{ fill: 'transparent' }}
                    content={<CustomTooltip />}
                  />
                  <Legend formatter={(value) => <span className="text-neutral-900 font-medium">{value}</span>} />
                  <ReferenceLine y={goal} stroke="red" strokeDasharray="3 3" label={{ value: 'Goal', position: 'right', fill: 'red', fontSize: 12 }} />
                  <Bar name="Total Pipeline" dataKey="totalRevenue" fill="#e5e5e5" radius={[4, 4, 0, 0]} />
                  <Bar name="Weighted Forecast" dataKey="weightedRevenue" fill="#171717" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
  );
}

